import { useState } from "react";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, CancelButton } from "ui-components/IconButton";
import { Success, Error_ } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { useCustomerValue } from "providers/CustomerContext";
import {
  getPutawayList,
  getPutawayStatusCount,
  getPutawayQueueStatusCount,
  createPutaway,
} from "services/data.inbound.service";
import PalletItems from "./PalletItems";
import BasicQueueTabs from "ui-components/BasicQueueTabs";
import PutawayLog from "./PutawayLog";
import { confirmPutaway } from "services/data.inbound.service";
import { LoadingDialog } from "ui-components/Alert";
import { TableCard } from "ui-components/Layout";
import { cancelPutaway } from "services/data.inbound.service";
import { cancelPalletize } from "services/data.inbound.service";
import { PrintButton } from "ui-components/IconButton";
import { getPutawayReport } from "services/data.report.service";
import { useTeamValue } from "providers/TeamContext";
import { MessageDialog } from "ui-components/Alert";
function getLabel(status) {
  if (status === "Open") return "R";
  else if (status === "Putaway Generated") return "O";
  else if (status === "Putaway Confirmed") return "P";
  else return "";
}
function getStatus(label) {
  if (label === "R") return "Open";
  else if (label === "O") return "Putaway Generated";
  else if (label === "P") return "Putaway Confirmed";
  else return "Open";
}

function PutawayList() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const enabled = customerCode !== null;
  const navigate = useNavigate();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState(getStatus(location.state));
  const [queueStatus, setQueueStatus] = useState("");
  const [tabs, setTabs] = useState([]);
  const [queueTabs, setQueueTabs] = useState([]);
  const [click, setClick] = useState(false)
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const listStatus = getLabel(status);

  const addPermission =
    allowAll ||
    (Boolean(permissions["Putaway"]) &&
      permissions["Putaway"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Putaway"]) &&
      permissions["Putaway"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Putaway"]) &&
      permissions["Putaway"].findIndex((b) => b === "deactivate") !== -1);

  const { isRefetching } = useQuery({
    queryKey: ["putaway-list", { customerCode, status, queueStatus }],
    queryFn: () => getPutawayList({ customerCode, status: listStatus, queueStatus }),
    enabled,
    onSuccess: (data) => setData(data.data.data),
    onError,
    refetchInterval: 30000,
  });

  useQuery({
    queryKey: ["putaway-list", "status", { customerCode, status, queueStatus }],
    queryFn: () => getPutawayStatusCount({ customerCode }),
    enabled,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
    refetchInterval: 30000,
  });

  useQuery({
    queryKey: ["putaway-list", "queue-status", { customerCode, status, queueStatus }],
    queryFn: () => getPutawayQueueStatusCount({ customerCode }),
    enabled: customerCode !== null && status === "Open",
    onSuccess: (data) => setQueueTabs(data.data.data),
    onError,
  });
  const createMutation = useMutation(createPutaway, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["putaway-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const cancelMutation = useMutation(cancelPalletize, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["putaway-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const createStatus = createMutation.status === "loading";

  const downloadReportMutation = useMutation(getPutawayReport, {
    onSuccess: (response) => {
      const fileType = response.headers["content-type"];
      const blob = new Blob([response.data], {
        type: fileType,
      });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url);
      w.print();
      window.URL.revokeObjectURL(url);
    },
    onError: () => setAlert(<Error_ content="The report file not found" onChange={setAlert} />),
  });
  const downloadStatus = downloadReportMutation.status === "loading";

  const confirmPutawayMutation = useMutation(confirmPutaway, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["putaway-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const cancelPutawayMutation = useMutation(cancelPutaway, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["putaway-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const confirmPutawayStatus = confirmPutawayMutation.status === "loading";

  const [palletRequest, setPalletRequest] = useState(null);
  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const palletProp = { open, setOpen, palletRequest, setPalletRequest };
  const logProp = { open, setOpen, logRequest, setLogRequest };

  const onCancelPalletize = (req) => {
    setDialog(
      <MessageDialog
        title={`Cancel Palletize ${req.recvCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          cancelMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };
  const onCancelPutaway = (req) => {
    setDialog(
      <MessageDialog
        title={`Cancel Putaway ${req.putawayNumber}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          cancelPutawayMutation.mutate({ putawayId: req.putawayId });
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    {
      header: "Receiving Code",
      accessorKey: "recvCode",
      cell: ({ row }) => {
        const { recvCode } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen(true);
              setPalletRequest({ recvCode });
            }}
          >
            {recvCode}
          </span>
        );
      },
    },
    { header: "Receiving Date", accessorKey: "dateReceived" },
    { header: "Warehouse Code", accessorKey: "warehouseCode" },
    { header: "Seller Company", accessorKey: "sellerCompany" },
    { header: "Vendor Code", accessorKey: "vendorCode" },
    { header: "Queue Date", accessorKey: "queueDate" },
    { header: "Msg", accessorKey: "message" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { recvCode, customerCode } = row.original;
        return status === "Open" && queueStatus !== "NEW" ? (
          <>
            <CancelButton
              title="cancel palletize"
              onClick={() => onCancelPalletize({ recvCode })}
            />
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={() => {
                setClick(true) 
                createMutation.mutate({ recvCode, customerCode },{
                  onSettled:() => setClick(false)
                })
              }}
              disabled={click || createStatus || !addPermission}
            >
              putaway
            </MDButton>
          </>
        ) : (
          <></>
        );
      },
    },
  ];

  const putawayColumns = [
    {
      header: "Putaway Number",
      accessorKey: "putawayNumber",
      width: "150px",
      cell: ({ row }) => {
        const { putawayId, putawayNumber } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen(true);
              setLogRequest({ putawayId });
            }}
          >
            {putawayNumber}
          </span>
        );
      },
    },
    { header: "Receiving Code", accessorKey: "recvCode" },
    { header: "Vendor Code", accessorKey: "vendorCode" },
    { header: "Warehouse Code", accessorKey: "warehouseCode" },
  ];
  const putawayGeneratedColumns = [
    ...putawayColumns,
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { putawayId } = row.original;
        return (
          <>
            <PrintButton
              title="print putaway"
              onClick={() => {
                downloadReportMutation.mutate({
                  customerCode,
                  putawayNumber: row.original.putawayNumber,
                  reportType: "PDF",
                });
              }}
            />
            <EditButton
              title="edit putaway"
              disabled={!editPermission}
              onClick={() => navigate("/inbound/putaway/edit", { state: { putawayId } })}
            />
            <CancelButton
              title="cancel putaway"
              disabled={!deactivatePermission}
              onClick={() =>
                onCancelPutaway({ putawayId, putawayNumber: row.original.putawayNumber })
              }
            />
            <MDButton
              sx={{ ml: "10px" }}
              variant="gradient"
              color="info"
              size="small"
              onClick={() => {
                setClick(true)
                confirmPutawayMutation.mutate({ putawayId },{
                  onSettled:() => setClick(false)
                })
              }}
              disabled={click || confirmPutawayStatus || !editPermission}
            >
              confirm
            </MDButton>
          </>
        );
      },
    },
  ];

  function getColumns() {
    if (status === "Open") return columns;
    else if (status === "Putaway Generated") return putawayGeneratedColumns;
    else return putawayColumns;
  }
  const rows = data;
  const date = new Date();
  return (
    <ListLayout>
      {(isRefetching || createStatus || confirmPutawayStatus || downloadStatus) && (
        <LoadingDialog />
      )}

      {dialog}
      {status === "Open" ? <PalletItems {...palletProp} /> : <PutawayLog {...logProp} />}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
        {status === "Open" && queueTabs.length > 0 && (
          <BasicQueueTabs
            queueTabs={queueTabs}
            queueStatus={queueStatus}
            setQueueStatus={setQueueStatus}
          />
        )}
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="flex-end">
          <MDBox padding=".5rem" fontSize="1.1rem">
            Last Updated: {date.toLocaleString()}
          </MDBox>
        </MDBox>
        <BasicTable columns={getColumns()} data={rows} />
      </TableCard>
    </ListLayout>
  );
}

export default PutawayList;
